<template>
  <div
    ref="wrapper"
    class="video-player__container"
  >
    <video
      v-cloak
      :id="videoIdentifier"
      ref="video"
      :autoplay="autoplay"
      :loop="video.enableLoop"
      muted
      playsinline
      :video-identifier="videoIdentifier"
    >
      <source
        :src="getVideoSource().url"
        :type="getVideoSource().mime_type"
      >
    </video>
  </div>
</template>

<script>

/**
 * Vue: Video player (video-player)
 * -----------------------------------------------------------------------------
 * Video player.
 *
 * @param {Object} video - Object containing two Shopify video objects (desktop
 * and mobile), * autoplay and loop settings.
 * @param {String} videoIdentifier - Unique identifier for the video player.
 */
import { nextTick } from 'vue'
import { setIntersectionObserver } from '~/helpers/general'

export default {
  name: 'VideoPlayer',

  props: {
    video: {
      type: Object,
      default: () => ({
        enableAutoplay: false,
        enableLoop: false,
        source: {},
      }),
    },
    videoIdentifier: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      autoplay: false,
      loaded: false,
      playing: false,
    }
  },

  watch: {

    /**
     * Handle changes to the video's loaded state.
     * - Set playing state to true.
     * - Emit an event to indicate that the specific video has loaded.
     * - Set event bus listeners.
     * - Set up the auto-pause observer.
     */
    loaded() {
      if (!this.loaded) {
        return
      }

      nextTick().then(() => {
        cnvs.EventBus.emit(`video-player:${this.videoIdentifier}:loaded`)
        this.setEventBusListeners()
        this.setPauseObserver()
      })
    },

    /**
     * Handle changes to the component's playing state.
     * - Toggle the state of the video player.
     * - Emit an event with the updated state.
     */
    playing() {
      if (this.playing) {
        this.$refs.video.play()
      } else {
        this.$refs.video.pause()
      }

      cnvs.EventBus.emit(
        `video-player:${this.videoIdentifier}:playing`,
        this.playing,
      )
    },
  },

  mounted() {
    this.initialiseVideo()
  },

  methods: {

    /**
     * Initialise autoplay, loaded and playing variables.
     */
    initialiseVideo() {

      /**
       * Prevent video autoplay if prefers reduced motion, otherwise use the
       * theme setting.
       */
      this.autoplay = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? false
        : this.video.enableAutoplay

      /**
       * Use `loadeddata` event to determine if the video has loaded.
       * - Call `video.load()` for iOS Safari, otherwise the `loadeddata` event
       *   listener will not trigger. Note that other workarounds
       *   (`loadedmetadata`, `video.onloadeddata`, `video.onloadedmetadata`)
       *   did not work.
       * https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/loadeddata_event
       */
      this.$refs.video.load()

      this.$refs.video.addEventListener('loadeddata', () => {
        this.loaded = true
        this.playing = this.autoplay
      })
    },

    /**
     * Returns the Object of the video_source array that contains either
     * the mp4 or mov version.
     *
     * @param {String} resolution - target resolution (desktop or mobile)
     * @returns {Object} - video source object
     */
    getVideoSource() {
      const targetIndex = this.findSourceUrl(this.video.source.sources)

      return this.video.source.sources[targetIndex]
    },

    /**
     * Finds the mp4 or mov file to render the video.
     *
     * @param {Array} videoData = array of video.sources
     * @returns {Number} - index of target video
     */
    findSourceUrl(videoData) {
      const url = (source) => {
        return source.format.includes('mp4') || source.format.includes('mov')
      }

      return videoData.findIndex(url)
    },

    /**
     * Automatically pause/play the video when scrolled into/(almost) out
     * of view.
     * - Don't run if autoplay is disabled (otherwise it will force autoplay).
     */
    handlePauseIntersection(entries) {
      if (!this.autoplay) {
        return
      }

      entries.forEach(entry => {
        this.playing = entry.intersectionRatio > 0.25
      })
    },

    /**
     * Listen for events to update the component's playing state.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen(
        `video-player:${this.videoIdentifier}:set-playing`,
        (playing) => {
          this.playing = playing
        },
      )
    },

    /**
     * Set an observer to automatically pause the video when (almost) out
     * of view.
     */
    setPauseObserver() {
      setIntersectionObserver({
        callback: this.handlePauseIntersection,
        element: this.$refs.wrapper,
        threshold: 0.25,
      })
    },
  },
}
</script>

<style lang="scss">
@import './video-player';
</style>
